import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/FirebaseAuthContext';
import { useUsage } from '../contexts/UsageContext';
import { LoadingSpinner } from './LoadingSpinner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}


interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  problemId: string;
  problemTitle: string;
}

export const ChatModal: React.FC<ChatModalProps> = ({
  isOpen,
  onClose,
  problemId,
  problemTitle,
}) => {
  const { isAuthenticated } = useAuth();
  const { openPaywall, refreshUsage } = useUsage();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [copiedCodeId, setCopiedCodeId] = useState<string | null>(null);

  const loadChatHistory = useCallback(async () => {
    try {
      setIsLoadingHistory(true);
      const response = await apiService.getChatHistory(problemId);
      if (response.success && response.data) {
        setMessages(response.data.messages || []);
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
      // Don't show error for missing history - just start with empty messages
    } finally {
      setIsLoadingHistory(false);
    }
  }, [problemId]);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
    }, 100);
  }, []);

  // Load chat history when modal opens
  useEffect(() => {
    if (isOpen && isAuthenticated && problemId) {
      loadChatHistory();
    }
  }, [isOpen, isAuthenticated, problemId, loadChatHistory]);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMessages([]);
      setInputMessage('');
      setError(null);
      setIsLoading(false);
      setIsLoadingHistory(false);
      setShowSavePrompt(false);
      setHasUnsavedChanges(false);
    }
  }, [isOpen]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || isLoading || !isAuthenticated) {
      return;
    }

    const userMessage: ChatMessage = {
      id: `temp_${Date.now()}`,
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError(null);
    setHasUnsavedChanges(true);

    try {
      const response = await apiService.sendChatMessage(problemId, inputMessage.trim());
      
      if (response.success && response.data) {
        const assistantMessage: ChatMessage = {
          id: `assistant_${Date.now()}`,
          role: 'assistant',
          content: response.data.message,
          timestamp: new Date(),
        };

        setMessages(prev => [...prev, assistantMessage]);

        // Refresh usage to update credits display after successful AI feature usage
        refreshUsage().catch(err => {
          console.warn('Failed to refresh usage after chat:', err);
          // Non-critical error - don't show to user
        });
      } else {
        setError('Failed to send message. Please try again.');
      }
    } catch (error: any) {
      console.error('Failed to send message:', error);

      // Check if this is a usage limit error (402 Payment Required)
      const isUsageLimitError = error?.status === 402 ||
                                error?.message?.includes('usage-limit-exceeded') ||
                                error?.originalError?.status === 402;

      if (isUsageLimitError) {
        // Open paywall modal instead of showing generic error
        openPaywall('no-credits', 'ai-tutor-chat');
      } else {
        // Show generic error for other errors
        setError(error.message || 'Failed to send message. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges && messages.length > 0) {
      setShowSavePrompt(true);
    } else {
      onClose();
    }
  };

  const handleSaveAndClose = async () => {
    try {
      await apiService.saveChatHistory(problemId);
      setHasUnsavedChanges(false);
      setShowSavePrompt(false);
      onClose();
    } catch (error) {
      console.error('Failed to save chat history:', error);
      setError('Failed to save chat history. Please try again.');
    }
  };

  const handleDiscardAndClose = () => {
    setShowSavePrompt(false);
    onClose();
  };

  const copyToClipboard = async (text: string, type: 'message' | 'code', id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'message') {
        setCopiedMessageId(id);
        setTimeout(() => setCopiedMessageId(null), 2000);
      } else {
        setCopiedCodeId(id);
        setTimeout(() => setCopiedCodeId(null), 2000);
      }
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const formatTimestamp = (timestamp: Date | { _seconds: number; _nanoseconds: number }) => {
    // Handle Firestore Timestamp objects (from backend) which have _seconds and _nanoseconds
    let date: Date;
    if (timestamp && typeof timestamp === 'object' && '_seconds' in timestamp) {
      date = new Date(timestamp._seconds * 1000);
    } else {
      date = new Date(timestamp);
    }

    // Check if date is valid before formatting
    if (isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-gray-900 bg-opacity-10 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-2xl border-4 border-gray-400 w-full max-w-4xl mx-4 h-[80vh] flex flex-col" style={{boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)'}}>
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">🤖 AI Assistant</h3>
            <div className="text-sm text-gray-600">
              <span>Problem: {problemTitle}</span>
            </div>
          </div>
          <button 
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
            aria-label="Close chat"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {!isAuthenticated ? (
            <div className="text-center p-8">
              <p className="text-gray-600">Please sign in to use the AI assistant.</p>
            </div>
          ) : isLoadingHistory ? (
            <div className="text-center p-8 flex items-center justify-center space-x-2">
              <LoadingSpinner size="small" />
              <span className="text-gray-600">Loading chat history...</span>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center p-8 bg-gray-50 rounded-lg">
              <p className="text-gray-700 mb-4">👋 Hello! I'm your AI assistant for algorithms and coding problems.</p>
              <p className="text-gray-600 mb-4">Feel free to ask me questions about:</p>
              <ul className="text-left text-gray-600 space-y-1 mb-4 max-w-md mx-auto">
                <li>• Algorithm approaches and patterns</li>
                <li>• Data structures and their use cases</li>
                <li>• Problem-solving strategies</li>
                <li>• Code optimization techniques</li>
                <li>• Interview preparation tips</li>
              </ul>
              <p className="text-gray-700">How can I help you with this problem?</p>
            </div>
          ) : (
            messages.map((message, index) => (
              <div key={message.id || index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-lg ${
                  message.role === 'user' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-900'
                }`}>
                  {message.role === 'assistant' ? (
                    <div className="markdown-content">
                      <div className="flex justify-end mb-2">
                        <button
                          onClick={() => copyToClipboard(message.content, 'message', message.id)}
                          className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-200 transition-colors"
                          title="Copy message"
                        >
                          {copiedMessageId === message.id ? (
                            <span className="text-green-600">✓</span>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          )}
                        </button>
                      </div>
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                        code: ({ className, children, ...props }) => {
                          const match = /language-(\w+)/.exec(className || '');
                          const codeContent = String(children).replace(/\n$/, '');
                          const codeId = `${message.id}-${index}-${Math.random()}`;
                          
                          return match ? (
                            <div className="relative my-2">
                              <div className="flex justify-between items-center bg-gray-700 text-gray-300 px-3 py-2 text-xs rounded-t-lg">
                                <span>{match[1]}</span>
                                <button
                                  onClick={() => copyToClipboard(codeContent, 'code', codeId)}
                                  className="text-gray-400 hover:text-gray-200 p-1 rounded hover:bg-gray-600 transition-colors"
                                  title="Copy code"
                                >
                                  {copiedCodeId === codeId ? (
                                    <span className="text-green-400">✓</span>
                                  ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                  )}
                                </button>
                              </div>
                              <pre className="bg-gray-800 text-white p-3 rounded-lg overflow-x-auto">
                                <code className={className}>
                                  {children}
                                </code>
                              </pre>
                            </div>
                          ) : (
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                              {children}
                            </code>
                          );
                        },
                        pre: ({ children }) => <div>{children}</div>,
                        blockquote: ({ children }) => (
                          <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-700">
                            {children}
                          </blockquote>
                        ),
                        ul: ({ children }) => (
                          <ul className="list-disc list-inside space-y-1 ml-4">
                            {children}
                          </ul>
                        ),
                        ol: ({ children }) => (
                          <ol className="list-decimal list-inside space-y-1 ml-4">
                            {children}
                          </ol>
                        ),
                        strong: ({ children }) => (
                          <strong className="font-semibold text-gray-900">
                            {children}
                          </strong>
                        ),
                        em: ({ children }) => (
                          <em className="italic text-gray-800">
                            {children}
                          </em>
                        ),
                        h1: ({ children }) => (
                          <h1 className="text-xl font-bold mt-4 mb-2 text-gray-900">
                            {children}
                          </h1>
                        ),
                        h2: ({ children }) => (
                          <h2 className="text-lg font-semibold mt-3 mb-2 text-gray-900">
                            {children}
                          </h2>
                        ),
                        h3: ({ children }) => (
                          <h3 className="text-base font-semibold mt-2 mb-1 text-gray-900">
                            {children}
                          </h3>
                        ),
                        p: ({ children }) => (
                          <p className="mb-2 leading-relaxed text-left block w-full">
                            {children}
                          </p>
                        ),
                        table: ({ children }) => (
                          <table className="border-collapse border border-gray-300 w-full mt-2 mb-2">
                            {children}
                          </table>
                        ),
                        th: ({ children }) => (
                          <th className="border border-gray-300 px-2 py-1 bg-gray-100 font-semibold text-left">
                            {children}
                          </th>
                        ),
                        td: ({ children }) => (
                          <td className="border border-gray-300 px-2 py-1">
                            {children}
                          </td>
                        ),
                      }}
                      >
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <div>{message.content}</div>
                  )}
                  <div className="text-xs text-gray-500 mt-2">
                    {formatTimestamp(message.timestamp)}
                  </div>
                </div>
              </div>
            ))
          )}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="max-w-[80%] p-3 rounded-lg bg-gray-100 text-gray-900 flex items-center space-x-2">
                <LoadingSpinner size="small" />
                <span>Thinking...</span>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 flex items-center justify-between">
            <span>❌ {error}</span>
            <button 
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-100"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200">
          <div className="flex space-x-2">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={
                !isAuthenticated 
                  ? "Sign in to chat..." 
                  : "Ask me about algorithms, data structures, or this problem..."
              }
              disabled={!isAuthenticated || isLoading}
              className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              autoFocus
            />
            <button
              type="submit"
              disabled={!isAuthenticated || isLoading || !inputMessage.trim()}
              className="p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <span className="w-5 h-5 block">⏳</span>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
            </button>
          </div>
        </form>

        {hasUnsavedChanges && (
          <div className="p-2 bg-yellow-50 border-t border-yellow-200 text-yellow-700 text-sm text-center">
            <span>💾 You have unsaved changes</span>
          </div>
        )}
      </div>

      {showSavePrompt && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-15 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-2xl border-4 border-gray-400 max-w-sm w-full mx-4 p-6" style={{boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)'}}>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Save Chat History?</h4>
            <p className="text-sm text-gray-600 mb-6">Would you like to save your conversation for future reference?</p>
            <div className="flex space-x-3">
              <button
                onClick={handleSaveAndClose}
                className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                💾 Save & Close
              </button>
              <button
                onClick={handleDiscardAndClose}
                className="flex-1 px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
              >
                🗑️ Discard & Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};